import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Calendar as CalendarIcon,
  Filter,
  AlertTriangle,
  Users,
  Activity,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
  ZoomIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StudioGantt, { StudioConflictFeed } from "@/components/studio/StudioGantt";
import StudioInsights from "@/components/studio/StudioInsights";
import ShareLinkDialog from "@/components/studio/ShareLinkDialog";
import { studioApi, plansApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const todayIso = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const addDaysISO = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const diffDays = (a, b) => {
  const da = new Date(a + "T00:00:00Z");
  const db = new Date(b + "T00:00:00Z");
  return Math.round((db - da) / 86400000);
};

const PRESETS = [
  { label: "2 wks", days: 14 },
  { label: "4 wks", days: 28 },
  { label: "8 wks", days: 56 },
  { label: "12 wks", days: 84 },
];

const ZOOM_LEVELS = [1, 1.5, 2, 3, 5];
const ZOOM_STORAGE_KEY = "studio:zoom";

const readStoredZoom = () => {
  try {
    const v = parseFloat(localStorage.getItem(ZOOM_STORAGE_KEY) || "");
    return ZOOM_LEVELS.includes(v) ? v : 1;
  } catch {
    return 1;
  }
};

export default function StudioResourcesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";

  // Deep-link support: ?focus=<uid>&date=<iso> from Launchpad Studio Insights
  // narrows the initial view to the offending person + date window.
  const focusParam = searchParams.get("focus");
  const dateParam = searchParams.get("date");
  const initialDate = dateParam || todayIso();
  const initialStart = dateParam ? addDaysISO(dateParam, -3) : todayIso();
  const initialEnd = dateParam ? addDaysISO(dateParam, 10) : addDaysISO(todayIso(), 28);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);
  const [projectFilter, setProjectFilter] = useState("all");
  const [personFilter, setPersonFilter] = useState(focusParam || "all");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [zoom, setZoom] = useState(() => readStoredZoom());

  // Once we've consumed the deep-link params, strip them from the URL so a
  // user refresh doesn't keep re-applying the same focus.
  useEffect(() => {
    if (focusParam || dateParam) {
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      next.delete("date");
      setSearchParams(next, { replace: true });
      if (focusParam || dateParam) {
        toast.success(`Focused on ${initialDate}`);
      }
    }
  }, []);

  const handleZoomChange = (z) => {
    setZoom(z);
    try { localStorage.setItem(ZOOM_STORAGE_KEY, String(z)); } catch { /* ignore */ }
  };

  // Clicking a Studio Insight narrows the view onto the flagged person and
  // (when applicable) re-centres the date window so the issue is on screen.
  const handleInsightClick = (ins) => {
    if (!ins) return;
    if (ins.user_id) setPersonFilter(ins.user_id);
    if (ins.iso_date) {
      const newStart = addDaysISO(ins.iso_date, -3);
      const newEnd = addDaysISO(ins.iso_date, 10);
      setStart(newStart);
      setEnd(newEnd);
    }
    toast.success(`Focused on ${ins.message}`);
  };

  const fetchData = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const params = { start, end };
      if (projectFilter !== "all") params.project_ids = projectFilter;
      if (personFilter !== "all") params.user_ids = personFilter;
      const resp = await studioApi.getResources(params);
      setData(resp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load studio resources");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [start, end, projectFilter, personFilter]);

  // Studio polish — drag-to-rebook: drag a bar in the Gantt to a new day or
  // a new person row. Routes through the per-project plan-tasks PATCH so
  // cascade + auto-level + over-allocation conflicts all stay correct.
  const handleRebook = async (task, changes) => {
    try {
      const resp = await plansApi.updateTask(task.id, changes);
      const moves = [];
      if (changes.start) moves.push(`→ ${changes.start}`);
      if (changes.assigned_user_id) {
        const newP = (data?.people || []).find((p) => p.user_id === changes.assigned_user_id);
        if (newP) moves.push(`→ ${newP.name || newP.email}`);
      }
      const cascaded = resp?.cascaded_tasks?.length || 0;
      const refits = resp?.cascade_refits?.length || 0;
      let msg = `Rebooked "${task.name}" ${moves.join(" ")}`.trim();
      if (cascaded > 0) msg += ` · ${cascaded} cascaded`;
      if (refits > 0) msg += ` · ${refits} re-fit`;
      toast.success(msg);
      // Refresh the studio view so allocations + conflicts reflect the new state
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rebook task");
    }
  };

  const peopleById = useMemo(() => {
    const m = {};
    (data?.people || []).forEach((p) => { m[p.user_id] = p; });
    return m;
  }, [data?.people]);

  const projectFilterOptions = useMemo(() => {
    return data?.projects || [];
  }, [data?.projects]);

  const personFilterOptions = useMemo(() => {
    return data?.people || [];
  }, [data?.people]);

  const stats = useMemo(() => {
    const tasks = data?.tasks || [];
    const people = data?.people || [];
    const projects = data?.projects || [];
    const conflicts = data?.conflicts || [];
    const conflictPeople = new Set(conflicts.map((c) => c.user_id));
    return {
      assigned_people: people.length,
      tasks: tasks.length,
      projects: projects.length,
      conflict_days: conflicts.length,
      conflict_people: conflictPeople.size,
    };
  }, [data]);

  const shiftRange = (dir) => {
    const dur = diffDays(start, end) + 1;
    const next = addDaysISO(start, dir * dur);
    setStart(next);
    setEnd(addDaysISO(next, dur - 1));
  };

  const applyPreset = (days) => {
    setStart(todayIso());
    setEnd(addDaysISO(todayIso(), days));
  };

  if (!isAdmin) {
    return (
      <div
        className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16 text-center"
        data-testid="studio-admin-only"
      >
        <div className="text-overline text-zinc-500 mb-2">Studio Resource Manager</div>
        <h2 className="font-heading text-2xl font-light text-zinc-100 mb-3">
          Admin-only view
        </h2>
        <p className="text-sm text-zinc-500 max-w-md mx-auto">
          The Studio view aggregates resourcing across every project. Ask an admin for
          access.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10" data-testid="studio-page">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-overline text-amber-500/80 mb-2">Studio</div>
          <h2 className="font-heading text-3xl font-light text-zinc-100">
            Resource Manager
          </h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Macro view of every person&apos;s load across every project. Bars are colored
            per project. Red cells flag days where someone is over capacity.
          </p>
        </div>
        <div className="text-xs font-mono text-zinc-500 flex items-center gap-3">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="studio-share-open-btn"
            onClick={() => setShareDialogOpen(true)}
            className="border-zinc-800 text-zinc-300 hover:text-amber-500 hover:border-zinc-700 rounded-sm h-8 px-3"
          >
            <LinkIcon className="w-3.5 h-3.5 mr-1.5" />
            Share
          </Button>
          <div className="inline-flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-amber-500" />
            <span>
              {data?.range?.start} → {data?.range?.end} · cap {data?.capacity_hours_per_day || 8}h/day
            </span>
          </div>
        </div>
      </div>

      {/* ── Stat strip ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Assigned People" value={stats.assigned_people} icon={Users} />
        <StatCard label="Active Projects" value={stats.projects} dot="bg-amber-500" />
        <StatCard label="Tasks in Window" value={stats.tasks} dot="bg-teal-500" />
        {stats.conflict_days > 0 ? (
          <StatCard
            label="Conflict Days"
            value={stats.conflict_days}
            warn
            sub={`${stats.conflict_people} person${stats.conflict_people === 1 ? "" : "s"}`}
            testid="studio-stat-conflicts"
          />
        ) : (
          <StatCard label="Conflicts" value={0} dot="bg-emerald-500" />
        )}
      </div>

      {/* ── Toolbar ──────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid="studio-range-prev"
            onClick={() => shiftRange(-1)}
            className="h-8 w-8 rounded-sm border border-zinc-800 text-zinc-400 hover:text-amber-500 hover:border-zinc-700 transition-colors inline-flex items-center justify-center"
            title="Previous window"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
            <CalendarIcon className="w-3.5 h-3.5 text-zinc-500" />
            <Input
              type="date"
              data-testid="studio-start-input"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="bg-zinc-950 border-zinc-800 h-8 w-[140px] text-xs font-mono"
            />
            <span className="text-zinc-600">→</span>
            <Input
              type="date"
              data-testid="studio-end-input"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="bg-zinc-950 border-zinc-800 h-8 w-[140px] text-xs font-mono"
            />
          </div>
          <button
            type="button"
            data-testid="studio-range-next"
            onClick={() => shiftRange(1)}
            className="h-8 w-8 rounded-sm border border-zinc-800 text-zinc-400 hover:text-amber-500 hover:border-zinc-700 transition-colors inline-flex items-center justify-center"
            title="Next window"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {/* Preset chips */}
          <div className="flex items-center gap-1 ml-1">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                data-testid={`studio-preset-${p.days}`}
                onClick={() => applyPreset(p.days)}
                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wide rounded-sm border border-zinc-800 text-zinc-500 hover:text-amber-500 hover:border-zinc-700 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Zoom controls */}
          <div
            className="inline-flex items-center gap-1 border border-zinc-800 rounded-sm h-8 px-1.5"
            data-testid="studio-zoom-group"
            title="Zoom level"
          >
            <ZoomIn className="w-3 h-3 text-zinc-500 mr-0.5" />
            {ZOOM_LEVELS.map((z) => (
              <button
                key={z}
                type="button"
                data-testid={`studio-zoom-${String(z).replace(".", "_")}x`}
                onClick={() => handleZoomChange(z)}
                aria-pressed={zoom === z}
                className={`px-1.5 py-0.5 text-[10px] font-mono rounded-sm transition-colors ${
                  zoom === z
                    ? "bg-amber-500/15 text-amber-400"
                    : "text-zinc-500 hover:text-amber-500"
                }`}
              >
                {z}×
              </button>
            ))}
          </div>
          <Filter className="w-3.5 h-3.5 text-zinc-500" />
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger
              data-testid="studio-project-filter"
              className="w-[180px] bg-zinc-950 border-zinc-800 focus:ring-amber-500 h-8 text-xs"
            >
              <SelectValue placeholder="Project" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
              <SelectItem value="all" className="focus:bg-zinc-900">All projects</SelectItem>
              {projectFilterOptions.map((p) => (
                <SelectItem key={p.id} value={p.id} className="focus:bg-zinc-900">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-sm"
                      style={{ backgroundColor: p.color?.bar || "#71717a" }}
                    />
                    {p.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={personFilter} onValueChange={setPersonFilter}>
            <SelectTrigger
              data-testid="studio-person-filter"
              className="w-[180px] bg-zinc-950 border-zinc-800 focus:ring-amber-500 h-8 text-xs"
            >
              <SelectValue placeholder="Person" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
              <SelectItem value="all" className="focus:bg-zinc-900">All people</SelectItem>
              {personFilterOptions.map((p) => (
                <SelectItem key={p.user_id} value={p.user_id} className="focus:bg-zinc-900">
                  {p.name || p.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      {loading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
        </div>
      ) : !data ? (
        <div className="py-16 text-center text-sm text-zinc-500">
          Could not load resources. Try a different date range.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
          <StudioGantt
            people={data.people}
            tasks={data.tasks}
            conflicts={data.conflicts}
            rangeStart={data.range.start}
            rangeEnd={data.range.end}
            capacity={data.capacity_hours_per_day}
            allocations={data.allocations}
            onRebook={handleRebook}
            zoom={zoom}
          />
          <div className="space-y-3">
            <StudioInsights
              people={data.people}
              tasks={data.tasks}
              allocations={data.allocations}
              conflicts={data.conflicts}
              capacity={data.capacity_hours_per_day}
              onInsightClick={handleInsightClick}
            />
            <ProjectLegend projects={data.projects} />
            <StudioConflictFeed
              conflicts={data.conflicts}
              peopleById={peopleById}
              onResolve={(projectId) => navigate(`/projects/${projectId}/plan`)}
            />
          </div>
        </div>
      )}

      <ShareLinkDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        defaultStart={start}
        defaultEnd={end}
        defaultProjectId={projectFilter}
        availableProjects={data?.projects || []}
        capacityHoursPerDay={data?.capacity_hours_per_day || 8}
      />
    </div>
  );
}

function ProjectLegend({ projects }) {
  if (!projects || projects.length === 0) return null;
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4" data-testid="studio-legend">
      <div className="text-overline text-zinc-500 mb-2">Project Palette</div>
      <ul className="space-y-1.5">
        {projects.map((p) => (
          <li key={p.id} className="flex items-center gap-2 text-xs">
            <span
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: p.color?.bar || "#71717a" }}
            />
            <span className="text-zinc-300 truncate" title={p.name}>{p.name}</span>
            {p.client && <span className="text-zinc-600 font-mono ml-auto truncate">{p.client}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, dot, warn, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-4 ${
        warn ? "border-orange-900/60 bg-orange-950/20" : "border-zinc-800 bg-zinc-950/60"
      }`}
    >
      <div className="flex items-center gap-2 text-overline mb-2">
        {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
        {Icon && <Icon className={`w-3 h-3 ${warn ? "text-orange-400" : "text-zinc-500"}`} />}
        {warn && <AlertTriangle className="w-3 h-3 text-orange-400" />}
        <span className={warn ? "text-orange-400" : "text-zinc-500"}>{label}</span>
      </div>
      <div className={`font-mono text-2xl ${warn ? "text-orange-300" : "text-zinc-100"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}
